import { useAgent, useConfigureSuggestions, useCopilotChatConfiguration, useSuggestions } from '@copilotkitnext/react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import localeService from 'src/dataServices/LocaleService';
import { RootState } from 'src/state/reduxStore';

/**
 * Configures and returns the chat follow-up suggestions for the support agent.
 *
 * Behaviour:
 *  - Registers dynamic follow-up suggestions (available after the first message
 *    exchange) using the copilot config from Redux.
 *  - Clears any displayed suggestions while the agent is responding so stale
 *    suggestions are not shown.
 *  - Appends the static "create support ticket" suggestion once auto-generated
 *    suggestions are present.
 */
export const useCopilotSuggestions = () => {
  const { agent } = useAgent({ agentId: 'support_agent' });
  const chatConfig = useCopilotChatConfiguration();
  const agentId = chatConfig?.agentId ?? 'default';

  const copilotConfig = useSelector((state: RootState) => state.agentVerification.copilotConfig);

  useConfigureSuggestions({
    instructions: copilotConfig.followUpSuggestionInstructions,
    maxSuggestions: Math.max(copilotConfig.followUpSuggestionMaxCount - 1, copilotConfig.followUpSuggestionMinCount, 1),
    minSuggestions: copilotConfig.followUpSuggestionMinCount,
    available: 'after-first-message',
    providerAgentId: agentId,
    consumerAgentId: agentId
  });

  const { suggestions: autoSuggestions, clearSuggestions } = useSuggestions({ agentId });

  // Clear displayed suggestions when the agent starts responding to a new message.
  React.useEffect(() => {
    if (agent?.isRunning) {
      clearSuggestions();
    }
  }, [agent?.isRunning, clearSuggestions]);

  return React.useMemo(() => {
    // Show suggestions only when auto-suggestions are present (i.e., after the
    // first message exchange) and the support ticket form is not active.
    if (autoSuggestions.length === 0) {
      return [];
    }
    const supportTicketSuggestion = {
      title: localeService.getText('ContactUs', 'ContactUsStaticSuggestionTitle1') ?? '',
      message: localeService.getText('ContactUs', 'ContactUsStaticSuggestionMessage1') ?? '',
      isLoading: false
    };
    return [...autoSuggestions, supportTicketSuggestion];
  }, [autoSuggestions]);
};
